import { useTranslation } from 'react-i18next';
import Box from 'shell/components/Box';
import Icon from 'shell/components/Icon';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';
import Tooltip from 'shell/components/Tooltip';

const EVIDENCE_COLORS = Object.freeze({
    supporting: '#2FD494',
    issue: '#F87472',
    uncertain: '#9E9E9E',
});

export const evidenceColor = (status) => EVIDENCE_COLORS[status] || EVIDENCE_COLORS.uncertain;

export const EvidenceStatusTag = ({ status = 'uncertain', legend = false }) => {
    const { t } = useTranslation('assignment');
    const labels = {
        supporting: t('video-grading.supporting', 'Supporting'),
        issue: t('video-grading.issue', 'Issue'),
        uncertain: t('video-grading.uncertain', 'Uncertain'),
    };
    const descriptions = {
        supporting: t(
            'video-grading.supporting-description',
            'Matches a defined look-for with specific, citable evidence.'
        ),
        issue: t('video-grading.issue-description', 'Identifies a violation or omission.'),
        uncertain: t('video-grading.uncertain-description', 'Evidence is unclear or conflicting. Review is needed.'),
    };
    return (
        <Tooltip title={descriptions[status] || descriptions.uncertain}>
            <Stack
                direction={'row'}
                alignItems={'center'}
                spacing={1}
                sx={(theme) => ({
                    borderRadius: '40px',
                    px: 2,
                    py: 1,
                    width: 'fit-content',
                    whiteSpace: 'nowrap',
                    background: legend
                        ? 'transparent'
                        : status === 'supporting'
                          ? theme.palette.ensightful.success[50]
                          : status === 'issue'
                            ? theme.palette.ensightful.error[100]
                            : theme.palette.ensightful.neutral[100],
                })}
            >
                {legend ? (
                    <Box
                        sx={(theme) => ({
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            background: evidenceColor(status),
                        })}
                    />
                ) : status === 'uncertain' ? (
                    <Text component={'span'} variant={'p3'} aria-hidden={'true'}>
                        ?
                    </Text>
                ) : (
                    <Icon
                        aria-hidden={'true'}
                        kind={status === 'supporting' ? 'checkmark' : 'exit'}
                        sx={{ fontSize: 10 }}
                    />
                )}
                <Text component={'span'} variant={'p3'}>
                    {labels[status] || labels.uncertain}
                </Text>
            </Stack>
        </Tooltip>
    );
};

export const SpeakerTag = ({ label }) => {
    if (!label) return null;
    const number = label.match(/(\d+)$/)?.[1];
    const hash = [...label].reduce((total, character) => total + character.codePointAt(0), 0);
    const index = number ? (((Number(number) - 1) % 10) + 10) % 10 : hash % 10;
    return (
        <Stack direction={'row'} alignItems={'center'} spacing={1}>
            <Box
                aria-hidden={'true'}
                sx={(theme) => {
                    const palette = theme.palette.ensightful;
                    const colors = [
                        palette.secondary[400],
                        palette.warning[300],
                        palette.success[700],
                        palette.warning[600],
                        palette.error[400],
                        palette.secondary[300],
                        palette.success[500],
                        palette.primary[400],
                        palette.error[700],
                        palette.warning[800],
                    ];
                    return { width: 10, height: 10, flexShrink: 0, borderRadius: '50%', background: colors[index] };
                }}
            />
            <Text component={'span'} variant={'p3'}>
                {label}
            </Text>
        </Stack>
    );
};
